import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  Grid,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Add, Delete, CloudUpload, Image, Description, Save, Category, LocalOffer, AttachFile, FormatAlignLeft, FormatAlignCenter, FormatAlignRight, Title as TitleIcon, PersonOutline, ShortText } from '@material-ui/icons';
import { IconButton, Divider, LinearProgress, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Editor } from '@tinymce/tinymce-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../main-component/Firebase';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { importDocumentAsHtml } from '../../utils/documentImport';
import {
  normalizeBlogTypography,
  getPloomEditorContentStyle,
  getPloomEditorColorMap,
  PLOOM_EDITOR_FONT_FORMATS,
  PLOOM_EDITOR_STYLE_FORMATS,
} from '../../utils/blogTypography';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '8px 0',
    maxWidth: '100%',
    margin: '0 auto',
    background: 'transparent',
    position: 'relative',
  },
  glassCard: {
    borderRadius: '18px',
    background: 'var(--bg-white)',
    border: '1px solid var(--color-ploom-alpha-10)',
    boxShadow: 'none',
    padding: 'clamp(1.15rem, 2.5vw, 1.65rem)',
    marginBottom: '1.25rem',
  },
  basicInformationCard: {
    border: 0,
    borderRadius: 0,
    background: 'transparent',
    boxShadow: 'none',
    padding: 0,
  },
  header: {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottom: 0,
  },
  title: {
    fontSize: 'clamp(28px, 4vw, 40px)',
    fontWeight: 400,
    color: 'var(--color-text-black)',
    margin: 0,
    lineHeight: 1.2,
    fontFamily: 'var(--font-title)',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: 'var(--color-text-dark)',
    marginBottom: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'var(--font-body)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    '& svg': {
      marginRight: '8px',
      color: 'var(--color-ploom)',
      fontSize: '1.2rem',
    }
  },
  dropzone: {
    border: '1.5px dashed var(--color-ploom-alpha-24)',
    borderRadius: '18px',
    padding: '2.5rem 1.5rem',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.25s',
    background: 'var(--color-ploom-alpha-08)',
    '&:hover': {
      background: 'var(--bg-light-ploom)',
      borderColor: 'var(--color-ploom)',
    }
  },
  editorContainer: {
    border: '1px solid var(--color-ploom-alpha-18)',
    borderRadius: '16px',
    overflow: 'hidden',
    marginTop: '1rem',
    '& .tox-tinymce': {
      height: '500px',
      '& iframe': {
        height: '100% !important',
      }
    }
  },
  imageContainer: {
    position: 'relative',
    borderRadius: '10px',
    overflow: 'hidden',
    cursor: 'pointer',
    '& img': {
      width: '100%',
      height: 'auto',
      display: 'block',
    }
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.48)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s',
    '&:hover': {
      opacity: 1,
    }
  },
  tagChip: {
    margin: '4px',
    backgroundColor: 'rgba(144, 77, 113, 0.1)',
    color: '#904d71',
    border: '1.5px solid rgba(144, 77, 113, 0.25)',
    '& .MuiChip-deleteIcon': {
      color: '#904d71',
    }
  },
  positionButton: {
    flex: 1,
    '&.MuiButton-contained': {
      backgroundColor: '#904d71',
      color: '#fff',
    },
    '&.MuiButton-outlined': {
      borderColor: '#904d71',
      color: '#904d71',
    }
  },
  modernButton: {
    background: 'var(--color-ploom)',
    color: 'var(--bg-white)',
    borderRadius: '999px',
    padding: '12px 32px',
    fontSize: '0.9rem',
    fontWeight: 600,
    textTransform: 'none',
    fontFamily: 'var(--font-body)',
    boxShadow: 'var(--shadow-btn)',
    transition: 'all 0.25s',
    '&:hover': {
      background: 'var(--color-ploom-dark)',
      boxShadow: 'var(--shadow-btn-hover)',
      transform: 'translateY(-1px)',
    }
  },
  progressBar: {
    borderRadius: '4px',
    height: '6px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  modernField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      minHeight: '48px',
      background: 'transparent',
      fontFamily: 'var(--font-body)',
      boxShadow: 'none',
      transition: 'background .2s ease',
      '& fieldset': {
        border: 0,
        borderRadius: '10px',
      },
      '&:hover': { background: 'var(--color-ploom-alpha-08)' },
      '&.Mui-focused fieldset': {
        border: 0,
      },
      '&.Mui-focused': { background: 'var(--color-ploom-alpha-08)' },
    },
    '& .MuiInputBase-input': {
      padding: '13px 12px',
      color: 'var(--color-text-body)',
      fontSize: '0.94rem',
    },
    '& .MuiFormHelperText-root': { fontFamily: 'var(--font-body)' },
  },
  modernFieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    height: '100%',
    padding: '14px 16px',
  },
  modernFieldsShell: {
    overflow: 'hidden',
    border: '1px solid var(--color-ploom-alpha-18)',
    borderRadius: '14px',
    background: 'var(--bg-white)',
    transition: 'border-color .2s ease, box-shadow .2s ease',
    '&:focus-within': {
      borderColor: 'var(--color-ploom)',
      boxShadow: '0 0 0 3px var(--color-ploom-alpha-10)',
    },
  },
  modernFieldGroupTitle: {
    borderBottom: '1px solid var(--color-ploom-alpha-10)',
  },
  modernFieldGroupSplit: {
    borderBottom: '1px solid var(--color-ploom-alpha-10)',
    [theme.breakpoints.up('sm')]: {
      borderRight: '1px solid var(--color-ploom-alpha-10)',
      borderBottom: 0,
    },
  },
  modernFieldLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    paddingLeft: '5px',
    fontFamily: 'var(--font-body)',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--color-ploom)',
    '& svg': {
      width: '18px',
      height: '18px',
      padding: '3px',
      borderRadius: '6px',
      background: 'var(--bg-light-ploom)',
    },
  }
}));

const EnhancedAdminBlogForm = ({ onSuccess, history }) => {
  const classes = useStyles();
  const user = useSelector((state) => state.user);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    author: user?.user?.displayName || '',
    subject: '',
    content: '',
    category: '',
    tags: [],
    coverImage: '',
    additionalImages: []
  });
  
  // UI state
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});
  
  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  
  // Image management
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePositions, setImagePositions] = useState({});
  useEffect(() => {
    loadCategoriesAndTags();
  }, []);

  const loadCategoriesAndTags = async () => {
    try {
      // Load existing categories using existing backend endpoint
      const categoriesResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE}/categories-blog/get`,
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
      
      if (categoriesResponse.data && Array.isArray(categoriesResponse.data)) {
        const categoryNames = categoriesResponse.data.map(cat => cat.name || cat);
        setCategories(categoryNames);
      } else if (categoriesResponse.data && categoriesResponse.data.success && categoriesResponse.data.data) {
        const categoryNames = categoriesResponse.data.data.map(cat => cat.name || cat);
        setCategories(categoryNames);
      } else {
        // Fallback to default categories
        setCategories([
          'Uleiurile esentiale la copii',
          'La bebelusi', 
          'La animale',
          'General',
          'Utilizari terapeutice',
          'Retete naturale'
        ]);
      }
      
      // Load existing tags using existing endpoint
      const tagsResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE}/tag/get`,
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
      
      if (tagsResponse.data && tagsResponse.data.data) {
        setTags(tagsResponse.data.data);
      } else if (Array.isArray(tagsResponse.data)) {
        setTags(tagsResponse.data);
      } else {
        setTags([]);
      }
    } catch (error) {
      console.error('Error loading categories and tags:', error);
      // Set default categories if API fails
      setCategories([
        'Uleiurile esentiale la copii',
        'La bebelusi',
        'La animale', 
        'General',
        'Utilizari terapeutice',
        'Retete naturale'
      ]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCoverImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Imaginea este prea mare. Mărimea maximă este 5MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          coverImage: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleMultipleImagesUpload = (event) => {
    const files = Array.from(event.target.files);
    const newImages = [];
    
    files.forEach((file, index) => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`Imaginea ${file.name} este prea mare. Mărimea maximă este 5MB.`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        newImages.push({
          id: Date.now() + index,
          src: e.target.result,
          name: file.name,
          position: 'center',
          insertAfter: ''
        });
        
        if (newImages.length === files.length) {
          setSelectedImages(prev => [...prev, ...newImages]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDocumentImport = async (event) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    try {
      const content = normalizeBlogTypography(await importDocumentAsHtml(file));
      editorRef.current?.setContent(content);
      setFormData((current) => ({ ...current, content }));
      toast.success('Documentul a fost importat și formatat cu stilurile Ploom.');
    } catch (error) {
      toast.error(error.message || 'Eroare la importul documentului.');
    } finally {
      input.value = '';
    }
  };

  const addNewCategory = async () => {
    if (!newCategory.trim()) return;
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/categories-blog/post`,
        {
          name: newCategory.trim(),
          description: `Categoria: ${newCategory.trim()}`,
          color: '#904d71'
        },
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      if (response.data && (response.data.id || response.data.name)) {
        setCategories(prev => [...prev, newCategory.trim()]);
        setFormData(prev => ({...prev, category: newCategory.trim()}));
        setNewCategory('');
        setCategoryDialogOpen(false);
        toast.success('Categoria a fost adăugată cu succes!');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      // For now, just add locally as fallback
      setCategories(prev => [...prev, newCategory.trim()]);
      setFormData(prev => ({...prev, category: newCategory.trim()}));
      setNewCategory('');
      setCategoryDialogOpen(false);
      toast.warning('Categoria a fost adăugată local (eroare la salvarea în backend)');
    }
  };

  const addNewTag = async () => {
    if (!newTag.trim()) return;
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/tag/post`,
        {
          name: newTag.trim(),
          category: 'blog',
          color: '#904d71',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      if (response.data && (response.data.data || response.data.id)) {
        const newTagData = response.data.data || response.data;
        setTags(prev => [...prev, newTagData]);
        setNewTag('');
        setTagDialogOpen(false);
        toast.success('Tag-ul a fost adăugat cu succes!');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error('Eroare la adăugarea tag-ului: ' + (error.response?.data?.message || error.message));
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleImagePositionChange = (imageId, position) => {
    setImagePositions(prev => ({
      ...prev,
      [imageId]: position
    }));
  };

  const insertImageInEditor = (image) => {
    if (editorRef.current) {
      const position = imagePositions[image.id] || 'center';
      const alignClass = position === 'left' ? 'align-left' : 
                        position === 'right' ? 'align-right' : 'align-center';
      
      const imageHtml = `<div class="blog-image ${alignClass}">
        <img src="${image.src}" alt="${image.name}" style="max-width: 100%; height: auto; margin: 16px 0;" />
      </div>`;
      
      editorRef.current.insertContent(imageHtml);
      toast.success('Imaginea a fost inserată în editor!');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Titlul este obligatoriu';
    if (!formData.subject.trim()) newErrors.subject = 'Subiectul este obligatoriu';
    if (!formData.content.trim()) newErrors.content = 'Conținutul este obligatoriu';
    if (!formData.category) newErrors.category = 'Categoria este obligatorie';
    if (!formData.coverImage) newErrors.coverImage = 'Imaginea de copertă este obligatorie';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Vă rugăm să completați toate câmpurile obligatorii.');
      return;
    }
    
    setLoading(true);
    setUploadProgress(0);
    
    try {
      // Get blog count for ID
      const countResponse = await axios.get(`${process.env.REACT_APP_API_BASE}/blog/get`);
      const blogId = countResponse.data.length + 1;
      
      // Upload cover image
      setUploadProgress(25);
      const coverImageRef = ref(storage, `blog/${blogId}/cover`);
      const coverImageBlob = await fetch(formData.coverImage).then(r => r.blob());
      await uploadBytes(coverImageRef, coverImageBlob);
      const coverImageUrl = await getDownloadURL(coverImageRef);
      
      // Upload additional images
      setUploadProgress(50);
      const additionalImageUrls = [];
      for (let i = 0; i < selectedImages.length; i++) {
        const imageRef = ref(storage, `blog/${blogId}/images/${i}`);
        const imageBlob = await fetch(selectedImages[i].src).then(r => r.blob());
        await uploadBytes(imageRef, imageBlob);
        const imageUrl = await getDownloadURL(imageRef);
        additionalImageUrls.push(imageUrl);
      }
      
      setUploadProgress(75);
      
      // Create blog post
      const blogData = {
        id: blogId,
        title: formData.title,
        subject: formData.subject,
        author: formData.author,
        content: normalizeBlogTypography(formData.content),
        category: formData.category,
        tags: formData.tags,
        coverImage: coverImageUrl,
        additionalImages: additionalImageUrls,
        createDate: Date.now(),
        comments: 0,
        views: 0,
        likes: 0,
        isPublished: true
      };
      
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/blog/post?id=${blogId}`,
        blogData,
        {
          headers: {
            Authorization: `Basic ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
      
      setUploadProgress(100);
      toast.success('Articolul a fost creat cu succes!');
      
      // Reset form
      setFormData({
        title: '',
        author: user?.user?.displayName || '',
        subject: '',
        content: '',
        category: '',
        tags: [],
        coverImage: '',
        additionalImages: []
      });
      setSelectedImages([]);
      setImagePositions({});
      
      if (onSuccess) onSuccess();
      if (history) history.push(`/blog-single-fullwidth/${blogId}`);
      
    } catch (error) {
      console.error('Error creating blog:', error);
      toast.error('Eroare la crearea articolului.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Box className={classes.root}>
      <Grid container spacing={4}>
        {/* Left Column - Main Form */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information */}
          <Card className={`${classes.glassCard} ${classes.basicInformationCard}`}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <Description /> Informații de Bază
            </Typography>
            
            <Box className={classes.modernFieldsShell}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Box className={`${classes.modernFieldGroup} ${classes.modernFieldGroupTitle}`}>
                  <label className={classes.modernFieldLabel} htmlFor="blog-title"><TitleIcon /> Titlu articol *</label>
                  <TextField
                    id="blog-title"
                    fullWidth
                    placeholder="Un titlu clar și memorabil"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    error={!!errors.title}
                    helperText={errors.title}
                    variant="outlined"
                    className={classes.modernField}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box className={`${classes.modernFieldGroup} ${classes.modernFieldGroupSplit}`}>
                  <label className={classes.modernFieldLabel} htmlFor="blog-author"><PersonOutline /> Autor</label>
                  <TextField
                    id="blog-author"
                    fullWidth
                    placeholder="Numele autorului"
                    value={formData.author}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    variant="outlined"
                    className={classes.modernField}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box className={classes.modernFieldGroup}>
                  <label className={classes.modernFieldLabel} htmlFor="blog-subject"><ShortText /> Subiect *</label>
                  <TextField
                    id="blog-subject"
                    fullWidth
                    placeholder="Tema principală a articolului"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    variant="outlined"
                    className={classes.modernField}
                  />
                </Box>
              </Grid>
            </Grid>
            </Box>
          </Card>

          {/* Document Import */}
          <Card className={classes.glassCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <AttachFile /> Import Document
            </Typography>
            
            <Box className={classes.dropzone} onClick={() => docInputRef.current?.click()}>
              <Description style={{ fontSize: 48, color: '#904d71', marginBottom: '16px' }} />
              <Typography variant="h6" style={{ color: '#904d71', marginBottom: '8px' }}>
                Importă document (.txt, .doc, .docx)
              </Typography>
              <Typography variant="body2" style={{ color: '#666' }}>
                Click pentru a selecta documentul de importat
              </Typography>
              <input
                ref={docInputRef}
                type="file"
                accept=".txt,.doc,.docx"
                onChange={handleDocumentImport}
                style={{ display: 'none' }}
              />
            </Box>
          </Card>

          {/* Content Editor */}
          <Card className={classes.glassCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <Description /> Conținut Articol *
            </Typography>
            
            <Box className={classes.editorContainer}>
              <Editor
                apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                onInit={(_, editor) => { editorRef.current = editor; }}
                value={formData.content}
                onEditorChange={(content) => handleInputChange('content', content)}
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount'
                  ],
                  toolbar: 'undo redo | styleselect formatselect fontselect | bold italic forecolor backcolor | ' +
                            'alignleft aligncenter alignright alignjustify | ' +
                            'bullist numlist outdent indent | removeformat | help',
                  font_formats: PLOOM_EDITOR_FONT_FORMATS,
                  style_formats: PLOOM_EDITOR_STYLE_FORMATS,
                  color_map: getPloomEditorColorMap(),
                  custom_colors: true,
                  content_style: getPloomEditorContentStyle(),
                  setup: (editor) => {
                    editor.on('blur', () => {
                      const normalized = normalizeBlogTypography(editor.getContent());
                      if (normalized !== editor.getContent()) editor.setContent(normalized);
                      handleInputChange('content', normalized);
                    });
                  },
                }}
              />
            </Box>
            {errors.content && (
              <Typography variant="caption" color="error" style={{ marginTop: '8px' }}>
                {errors.content}
              </Typography>
            )}
          </Card>
        </Grid>

        {/* Right Column - Media & Categories */}
        <Grid item xs={12} lg={4}>
          {/* Cover Image */}
          <Card className={classes.glassCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <Image /> Imagine Copertă *
            </Typography>
            
            {formData.coverImage ? (
              <Box className={classes.imageContainer}>
                <img 
                  src={formData.coverImage} 
                  alt="Cover" 
                  className={classes.imagePreview}
                />
                <Box className={classes.imageOverlay}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleInputChange('coverImage', '')}
                    startIcon={<Delete />}
                  >
                    Șterge
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box className={classes.dropzone} onClick={() => fileInputRef.current?.click()}>
                <CloudUpload style={{ fontSize: 48, color: '#904d71', marginBottom: '16px' }} />
                <Typography variant="h6" style={{ color: '#904d71', marginBottom: '8px' }}>
                  Încarcă imagine de copertă
                </Typography>
                <Typography variant="body2" style={{ color: '#666' }}>
                  Click pentru a selecta imaginea
                </Typography>
              </Box>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleCoverImageUpload}
              style={{ display: 'none' }}
            />
            
            {errors.coverImage && (
              <Typography variant="caption" color="error" style={{ marginTop: '8px' }}>
                {errors.coverImage}
              </Typography>
            )}
          </Card>

          {/* Additional Images */}
          <Card className={classes.glassCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <Image /> Imagini Adiționale
            </Typography>
            
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleMultipleImagesUpload}
              style={{ display: 'none' }}
              id="multiple-images-input"
            />
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => document.getElementById('multiple-images-input')?.click()}
              startIcon={<Add />}
              style={{ marginBottom: '16px' }}
            >
              Adaugă Imagini
            </Button>
            
            {selectedImages.map((image, index) => (
              <Box key={image.id} style={{ marginBottom: '16px' }}>
                <img 
                  src={image.src} 
                  alt={image.name}
                  style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '8px' }}
                />
                <Box style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
                  <Button
                    size="small"
                    variant={imagePositions[image.id] === 'left' ? 'contained' : 'outlined'}
                    onClick={() => handleImagePositionChange(image.id, 'left')}
                    className={classes.positionButton}
                  >
                    <FormatAlignLeft /> Stânga
                  </Button>
                  <Button
                    size="small"
                    variant={imagePositions[image.id] === 'center' ? 'contained' : 'outlined'}
                    onClick={() => handleImagePositionChange(image.id, 'center')}
                    className={classes.positionButton}
                  >
                    <FormatAlignCenter /> Centru
                  </Button>
                  <Button
                    size="small"
                    variant={imagePositions[image.id] === 'right' ? 'contained' : 'outlined'}
                    onClick={() => handleImagePositionChange(image.id, 'right')}
                    className={classes.positionButton}
                  >
                    <FormatAlignRight /> Dreapta
                  </Button>
                </Box>
                <Box style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => insertImageInEditor(image)}
                    style={{ flex: 1 }}
                  >
                    Inserează în Editor
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedImages(prev => prev.filter(img => img.id !== image.id))}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Card>

          {/* Category Selection */}
          <Card className={classes.glassCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <Category /> Categorie *
            </Typography>
            
            <FormControl fullWidth variant="outlined" style={{ marginBottom: '16px' }}>
              <InputLabel>Selectează categoria</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => {
                  if (e.target.value === 'add_new') {
                    setCategoryDialogOpen(true);
                  } else {
                    handleInputChange('category', e.target.value);
                  }
                }}
                label="Selectează categoria"
                error={!!errors.category}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value="add_new">
                  <Add /> Adaugă categorie nouă
                </MenuItem>
              </Select>
            </FormControl>
            
            {errors.category && (
              <Typography variant="caption" color="error">
                {errors.category}
              </Typography>
            )}
          </Card>

          {/* Tags */}
          <Card className={classes.glassCard}>
            <Typography variant="h6" className={classes.sectionTitle}>
              <LocalOffer /> Tag-uri
            </Typography>
            
            <Box style={{ marginBottom: '16px' }}>
              {formData.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => removeTag(tag)}
                  className={classes.tagChip}
                />
              ))}
            </Box>
            
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setTagDialogOpen(true)}
              startIcon={<Add />}
            >
              Adaugă Tag-uri
            </Button>
          </Card>

          {/* Submit Button */}
          <Card className={classes.glassCard}>
            {loading && (
              <Box style={{ marginBottom: '16px' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  className={classes.progressBar}
                />
                <Typography variant="caption" style={{ marginTop: '8px', display: 'block' }}>
                  Progres: {uploadProgress}%
                </Typography>
              </Box>
            )}
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              className={classes.modernButton}
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            >
              {loading ? 'Se publică...' : 'Publică Articolul'}
            </Button>
          </Card>
        </Grid>
      </Grid>

      {/* Category Dialog */}
      <Dialog 
        open={categoryDialogOpen} 
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adaugă Categorie Nouă</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nume categorie"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            margin="dense"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialogOpen(false)}>
            Anulează
          </Button>
          <Button 
            onClick={addNewCategory}
            variant="contained"
            className={classes.modernButton}
          >
            Adaugă
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tags Dialog */}
      <Dialog 
        open={tagDialogOpen} 
        onClose={() => setTagDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Selectează Tag-uri</DialogTitle>
        <DialogContent>
          <Box style={{ marginBottom: '16px' }}>
            <TextField
              fullWidth
              label="Adaugă tag nou"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              margin="dense"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <Button onClick={addNewTag} disabled={!newTag.trim()}>
                    <Add />
                  </Button>
                )
              }}
            />
          </Box>
          
          <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
            Tag-uri disponibile:
          </Typography>
          
          <Box>
            {tags.map((tag) => (
              <Chip
                key={tag.id || tag.name}
                label={tag.name || tag}
                onClick={() => {
                  const tagName = tag.name || tag;
                  if (!formData.tags.includes(tagName)) {
                    handleInputChange('tags', [...formData.tags, tagName]);
                  }
                }}
                className={classes.tagChip}
                style={{ 
                  margin: '4px',
                  opacity: formData.tags.includes(tag.name || tag) ? 0.5 : 1
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTagDialogOpen(false)}>
            Închide
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedAdminBlogForm;
